import React from 'react';
import { ViewMode } from '../types';

/**
 * Props for the ViewToggle component.
 */
interface ViewToggleProps {
  viewMode: ViewMode;
  onViewChange: (mode: ViewMode) => void;
}

/**
 * A component with three buttons (Grid, List, and Kanban) to toggle the view mode of the dashboard.
 * @param {ViewToggleProps} props - The props for the component.
 * @returns {React.ReactElement} - The rendered view toggle component.
 */
const ViewToggle: React.FC<ViewToggleProps> = ({ viewMode, onViewChange }) => {
  // Base classes for all buttons
  const baseClasses = 'p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200';
  // Classes for the active (selected) button
  const activeClasses = 'bg-blue-600 text-white shadow';
  // Classes for the inactive button
  const inactiveClasses = 'bg-gray-200 text-gray-600 hover:bg-gray-300';

  return (
    <div className="flex items-center bg-gray-200 rounded-lg p-1 space-x-1">
      <button
        onClick={() => onViewChange('grid')}
        className={`${baseClasses} ${viewMode === 'grid' ? activeClasses : inactiveClasses}`}
        aria-pressed={viewMode === 'grid'}
        title="Grid View"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      </button>
      <button
        onClick={() => onViewChange('list')}
        className={`${baseClasses} ${viewMode === 'list' ? activeClasses : inactiveClasses}`}
        aria-pressed={viewMode === 'list'}
        title="List View"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      </button>
      <button
        onClick={() => onViewChange('kanban')}
        className={`${baseClasses} ${viewMode === 'kanban' ? activeClasses : inactiveClasses}`}
        aria-pressed={viewMode === 'kanban'}
        title="Kanban View"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
        </svg>
      </button>
    </div>
  );
};

export default ViewToggle;
